import { HistogramValue, HistogramValueNew } from '@/api-client/models/indoorclimate/Contracts'
import { ChartComponent } from '..'
import { Axis, createAxis } from './axes'
import { Chart } from './chart'
import { Ticks, createTicks, responsiveLabels } from './ticks'
import { createTooltip } from './tooltip'
import { calculateLimitsFromValues, combineLimits } from './limits'
import { FormatOptions, formatter } from '@/components/AnalyticsDashboard/formatter'
import { DataSource } from '@/composables/useDataSources'
import { ColorScaleConfig, getColorForValue } from '@/utils/colorScale'
import { Bar, createBars } from './bars'
import { CircleHighlight, createCircleHightlight } from './circle-hightlight'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { LegendComponent } from '../../LegendBase'
import { createErrorText } from './utils'
import { transpose } from 'd3'

// The default format for the domain (x-axis for vertical bars, y-axis for horizontal bars)
const DEFAULT_DOMAIN_FORMAT: FormatOptions = {
  format: {
    type: 'intl-number',
    style: 'decimal',
    maximumFractionDigits: 2
  }
}

// The default format for the codomain (y-axis for vertical bars, x-axis for horizontal bars)
const DEFAULT_CODOMAIN_FORMAT: FormatOptions = {
  format: {
    type: 'intl-number',
    style: 'decimal',
    maximumFractionDigits: 2
  }
}

const DEFAULT_HORIZONTAL_AXIS: Axis<'top' | 'bottom'> = {
  position: 'bottom',
  showTicks: false,
  showLine: false
}

const DEFAULT_VERTICAL_AXIS: Axis<'left' | 'right'> = {
  position: 'left',
  showTicks: false,
  showLine: false,
  lines: true
}

export type Histogram = {
  verticalAxis: Partial<Axis<'left' | 'right'>>
  horizontalAxis?: Partial<Axis<'top' | 'bottom'>>
  codomainFormat?: FormatOptions
  codomainTicks?: Ticks
  domainFormat?: FormatOptions
  domainTicks?: Ticks
  colorScale?: string | ColorScaleConfig
  color?: string
  colors?: string[]
  dataSource?: string
  valueFilterTreshold?: number
  label: LocaleString
  legendColor: string
  missingDataMessage: LocaleString
}

const isInf = (value: number | string): value is string => typeof value === 'string'

const getXValues = (data: HistogramValue[], bucketSize: number): number[] =>
  data.map((value) => {
    let pos: number
    if (isInf(value.start) && !isInf(value.end)) {
      pos = value.end - bucketSize / 2
    } else if (isInf(value.end) && !isInf(value.start)) {
      pos = value.start + bucketSize / 2
    } else if (!isInf(value.end) && !isInf(value.start)) {
      pos = (value.start + value.end) / 2
    } else {
      throw new Error('Invalid histogram value')
    }
    return pos
  })
const getXValuesNew = (data: HistogramValueNew[], bucketSize: number): number[] =>
  data.map((value) => {
    let pos: number
    if (value.start === null && value.end !== null) {
      pos = value.end - bucketSize / 2
    } else if (value.end === null && value.start !== null) {
      pos = value.start + bucketSize / 2
    } else if (value.end !== null && value.start !== null) {
      pos = (value.start + value.end) / 2
    } else {
      throw new Error('Invalid histogram value')
    }
    return pos
  })
const getYValues = (data: HistogramValue[]): number[] => data.map((value) => value.value)

const valuesToPoints = (xValues: number[], yValues: number[]): [number, number][] =>
  xValues.map((x, i) => [x, yValues[i]] as [number, number])

const getData = (
  chart: Chart<'histogram'>,
  dataSources: Record<string, DataSource<HistogramValue[]> | undefined>
): HistogramValue[] => {
  const datasource = chart.dataSource
  if (!datasource) return []
  return [...(dataSources[datasource]?.data ?? [])]
}

const dataPointToLabel = (
  dataPoint: HistogramValue,
  options: FormatOptions,
  locale: string
): string => {
  if (isInf(dataPoint.start) && isInf(dataPoint.end)) {
    return '∞'
  } else if (isInf(dataPoint.start)) {
    return `< ${formatter(dataPoint.end, locale, options)}`
  } else if (isInf(dataPoint.end)) {
    return `> ${formatter(dataPoint.start, locale, options)}`
  } else {
    return `${formatter(dataPoint.start, locale, options)}`
  }
}

const dataPointToLabelNew = (
  dataPoint: HistogramValueNew,
  options: FormatOptions,
  locale: string
): string => {
  if (dataPoint.start === null && dataPoint.end === null) {
    return '∞'
  } else if (dataPoint.start === null && dataPoint.end !== null) {
    return `< ${formatter(dataPoint.end, locale, options)}`
  } else if (dataPoint.end === null && dataPoint.start !== null) {
    return `> ${formatter(dataPoint.start, locale, options)}`
  } else if (dataPoint.start !== null && dataPoint.end !== null) {
    return `${formatter(dataPoint.start, locale, options)}`
  } else {
    return '-'
  }
}

export function createHistogram(
  chart: Chart<'histogram'>,
  dataSources: Record<string, DataSource<HistogramValue[]> | undefined>,
  viewport: { width: number; height: number },
  locale: string,
  gradient = true
): ChartComponent[] | undefined {
  const datasource = chart.dataSource
  if (!datasource) return undefined
  if (!dataSources[datasource]?.data) return undefined
  const colorScale =
    typeof chart.colorScale === 'string'
      ? (dataSources[chart.colorScale]?.data as unknown as ColorScaleConfig)
      : chart.colorScale
  if (!colorScale && !chart.colors && !chart.color)
    return createErrorText(
      { en: 'Missing color configuration', sv: 'Saknar färgkonfiguration' },
      locale
    )
  const label = chart.label?.[locale] as string

  const data = getData(chart, dataSources)

  const start = data.findIndex((item) => item.value >= (chart.valueFilterTreshold ?? 0))
  const end = data
    .slice()
    .reverse()
    .findIndex((item) => item.value >= (chart.valueFilterTreshold ?? 0))
  const dataFiltered = data.slice(start, data.length - end)

  const bucketSize =
    dataFiltered.length >= 2 && !isInf(dataFiltered[1].end) && !isInf(dataFiltered[1].start)
      ? dataFiltered[1].end! - dataFiltered[1].start
      : 1.0
  const width = bucketSize * 0.8
  const xValues = getXValues(dataFiltered, bucketSize)
  const yValues = getYValues(dataFiltered)
  const points = valuesToPoints(xValues, yValues)

  if (points.length === 0) return createErrorText(chart.missingDataMessage, locale)

  const xFormat = chart.domainFormat ?? DEFAULT_DOMAIN_FORMAT
  const dataFormat = chart.codomainFormat ?? DEFAULT_CODOMAIN_FORMAT

  const horizontalAxis: Axis<'top' | 'bottom'> = {
    ...DEFAULT_HORIZONTAL_AXIS,
    ...chart.horizontalAxis
  }
  const verticalAxis: Axis<'left' | 'right'> = {
    ...DEFAULT_VERTICAL_AXIS,
    ...chart.verticalAxis
  }
  const padding = width / xValues.length
  const dataLimits = combineLimits([
    calculateLimitsFromValues(xValues.flat(), yValues.flat()),
    {
      x: [xValues[0] - width / 2 - padding, xValues[xValues.length - 1] + width / 2 + padding],
      y: [0, yValues[xValues.length - 1]]
    }
  ])
  const horizontalTicks: Ticks<'complete'> = responsiveLabels(
    {
      values: xValues,
      labels: dataFiltered.map((x) => dataPointToLabel(x, xFormat, locale))
    },
    viewport.width
  )
  const verticalTicks = createTicks(
    chart.codomainTicks ?? 'auto',
    dataLimits.y,
    yValues,
    dataFormat,
    locale
  )

  const tickLimits = calculateLimitsFromValues(horizontalTicks.values, verticalTicks.values)
  const limits = combineLimits([tickLimits, dataLimits])
  const colors = chart.color
    ? points.map(() => chart.color!)
    : chart.colors ??
      (colorScale ? points.map(([x]) => getColorForValue(colorScale, x).toHex()) : [])
  const bars: Bar[] = xValues.map((x, i) => ({
    color: colors[i],
    label,
    gradient,
    x,
    height: yValues[i]
  }))

  const highlights: CircleHighlight = {
    labels: colors.map(() => label),
    colors: colors,
    positions: points,
    offsets: points.map(([, y]) => [0, -y / 2]),
    visible: false
  }

  const barComps = createBars(transpose([bars]), width, 'vertical', limits)

  return [
    ...createTooltip(limits, dataFormat),
    ...createAxis(horizontalAxis, horizontalTicks, limits.x),
    ...createAxis(verticalAxis, verticalTicks, limits.y),
    ...createCircleHightlight(highlights, limits),
    ...barComps
  ]
}

export function createHistogramLegendComponents(
  chart: Chart<'histogram'>,
  locale: string
): LegendComponent[] {
  const label = chart.label?.[locale] as string
  return [
    {
      type: 'checkbox',
      label: label,
      color: chart.legendColor,
      styling: 'fill',
      initial: true
    }
  ]
}

export type HistogramConfig = {
  verticalAxis: Partial<Axis<'left' | 'right'>>
  horizontalAxis?: Partial<Axis<'top' | 'bottom'>>
  codomainFormat?: FormatOptions
  codomainTicks?: Ticks
  domainFormat?: FormatOptions
  domainTicks?: Ticks
  colorScale?: ColorScaleConfig
  color?: string
  colors?: string[]
  valueFilterTreshold?: number
  label: LocaleString
  legendColor: string
  missingDataMessage: LocaleString
}

export function createHistogramNew(
  chart: HistogramConfig,
  data: HistogramValueNew[],
  viewport: { width: number; height: number },
  locale: string,
  gradient = true
): ChartComponent[] | undefined {
  const colorScale = chart.colorScale
  if (!colorScale && !chart.colors && !chart.color)
    return createErrorText(
      { en: 'Missing color configuration', sv: 'Saknar färgkonfiguration' },
      locale
    )
  const label = chart.label?.[locale] as string

  const start = data.findIndex((item) => item.value >= (chart.valueFilterTreshold ?? 0))
  const end = data
    .slice()
    .reverse()
    .findIndex((item) => item.value >= (chart.valueFilterTreshold ?? 0))
  const dataFiltered = data.slice(start, data.length - end)

  const bucketSize =
    dataFiltered.length >= 2 && dataFiltered[1].end !== null && dataFiltered[1].start !== null
      ? dataFiltered[1].end! - dataFiltered[1].start
      : 1.0
  const width = bucketSize * 0.8
  const xValues = getXValuesNew(dataFiltered, bucketSize)
  const yValues = getYValues(dataFiltered as HistogramValue[])
  const points = valuesToPoints(xValues, yValues)

  if (points.length === 0) return createErrorText(chart.missingDataMessage, locale)

  const xFormat = chart.domainFormat ?? DEFAULT_DOMAIN_FORMAT
  const dataFormat = chart.codomainFormat ?? DEFAULT_CODOMAIN_FORMAT

  const horizontalAxis: Axis<'top' | 'bottom'> = {
    ...DEFAULT_HORIZONTAL_AXIS,
    ...chart.horizontalAxis
  }
  const verticalAxis: Axis<'left' | 'right'> = {
    ...DEFAULT_VERTICAL_AXIS,
    ...chart.verticalAxis
  }
  const padding = width / xValues.length
  const dataLimits = combineLimits([
    calculateLimitsFromValues(xValues.flat(), yValues.flat()),
    {
      x: [xValues[0] - width / 2 - padding, xValues[xValues.length - 1] + width / 2 + padding],
      y: [0, yValues[xValues.length - 1]]
    }
  ])
  const labels = dataFiltered.map((x) => dataPointToLabelNew(x, xFormat, locale))
  const horizontalTicks: Ticks<'complete'> = responsiveLabels(
    {
      values: xValues,
      labels
    },
    viewport.width
  )
  const verticalTicks = createTicks(
    chart.codomainTicks ?? 'auto',
    dataLimits.y,
    yValues,
    dataFormat,
    locale
  )

  const tickLimits = calculateLimitsFromValues(horizontalTicks.values, verticalTicks.values)
  const limits = combineLimits([tickLimits, dataLimits])
  const colors = chart.color
    ? points.map(() => chart.color!)
    : chart.colors ??
      (colorScale ? points.map(([x]) => getColorForValue(colorScale, x).toHex()) : [])
  const bars: Bar[] = xValues.map((x, i) => ({
    color: colors[i],
    label,
    gradient,
    x,
    height: yValues[i]
  }))

  const highlights: CircleHighlight = {
    labels: colors.map(() => label),
    colors: colors,
    values: points.map(([, y], i) => [labels[i], y]),
    positions: points,
    offsets: points.map(([, y]) => [0, -y / 2]),
    visible: false
  }

  const barComps = createBars(transpose([bars]), width, 'vertical', limits)

  return [
    ...createTooltip(limits, dataFormat, xFormat),
    ...createAxis(horizontalAxis, horizontalTicks, limits.x),
    ...createAxis(verticalAxis, verticalTicks, limits.y),
    ...createCircleHightlight(highlights, limits),
    ...barComps
  ]
}
